import { useEffect, useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { me, updateProfile, exportUserData } from '@/api';
import { Pencil, Camera, Phone, Mail, Download, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/auth';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

export default function ClientSettings(){
  const { t } = useLanguage();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [cap, setCap] = useState('');
  const [city, setCity] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    me().then(r => {
      setProfile(r.data);
      setName(r.data?.name || '');
      setSurname(r.data?.surname || '');
      setEmail(r.data?.email || '');
      setPhone(r.data?.phone || '');
      setStreet(r.data?.address?.street || '');
      setNumber(r.data?.address?.number || '');
      setCap(r.data?.address?.cap || '');
      setCity(r.data?.address?.city || '');
      setProfileImage(r.data?.profileImage || null);
    }).catch(() => {});
  }, []);

  const saveEdits = async () => {
    try {
      const payload = { name: name.trim(), surname: surname.trim(), phone: phone.trim(), address: { street: street.trim(), number: number.trim(), cap: cap.trim(), city: city.trim(), countryCode: 'IT' } };
      const res = await updateProfile(payload);
      setProfile(res.data);
      setEdit(false);
      toast.success(t('settings.dataUpdated'));
    } catch (err) { toast.error(t('settings.updateError')); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('settings.invalidImage')); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t('settings.imageTooLarge')); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const res = await updateProfile({ profileImage: reader.result });
        setProfile(res.data);
        setProfileImage(reader.result);
        toast.success(t('settings.profileImageUpdated'));
      } catch (err) { toast.error(t('settings.imageUpdateError')); }
    };
    reader.readAsDataURL(file);
  };

  const doHardDelete = () => {
    toast.success(t('settings.requestSent'));
    setDeleteOpen(false);
    setTimeout(() => nav('/'), 1500);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => nav('/cliente/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" /></Link>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Button variant="outline" size="sm" onClick={logout} className="text-slate-600">Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('settings.title')}</h1>
          <p className="text-slate-500 mt-1">{t('settings.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Profile */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="client-settings-profile">
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="h-20 w-20 rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    {profileImage ? <img src={profileImage} alt="Profilo" className="w-full h-full object-cover" /> : <span className="text-white text-3xl font-bold">{(profile?.name || 'U')[0].toUpperCase()}</span>}
                  </div>
                  <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#5848FF] text-white flex items-center justify-center shadow-lg" data-testid="change-profile-image"><Camera className="w-4 h-4" /></button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="profile-image-input" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl font-semibold text-slate-900">{profile?.name} {profile?.surname}</div>
                  <div className="text-sm text-slate-500">{profile?.email}</div>
                </div>
                <button onClick={() => setEdit(!edit)} className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600"><Pencil className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Personal data */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('settings.myData')}</h2>
              {!edit ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><div className="text-xs text-slate-500 mb-1">{t('settings.name')}</div><div className="text-sm font-medium text-slate-900">{profile?.name || '-'}</div></div>
                  <div><div className="text-xs text-slate-500 mb-1">{t('settings.surname')}</div><div className="text-sm font-medium text-slate-900">{profile?.surname || '-'}</div></div>
                  <div><div className="text-xs text-slate-500 mb-1">{t('settings.email')}</div><div className="text-sm font-medium text-slate-900">{profile?.email || '-'}</div></div>
                  <div><div className="text-xs text-slate-500 mb-1">{t('settings.phone')}</div><div className="text-sm font-medium text-slate-900">{profile?.phone || '-'}</div></div>
                  <div className="sm:col-span-2"><div className="text-xs text-slate-500 mb-1">{t('settings.address')}</div><div className="text-sm font-medium text-slate-900">{profile?.address ? `${profile.address.street} ${profile.address.number}, ${profile.address.cap} ${profile.address.city}` : '-'}</div></div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder={t('settings.name')} className="h-11 rounded-xl" />
                    <Input value={surname} onChange={e => setSurname(e.target.value)} placeholder={t('settings.surname')} className="h-11 rounded-xl" />
                    <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('settings.phone')} className="h-11 rounded-xl" />
                    <Input value={email} disabled className="h-11 rounded-xl bg-slate-50" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Input value={street} onChange={e => setStreet(e.target.value)} placeholder={t('settings.street')} className="h-11 rounded-xl" />
                    <Input value={number} onChange={e => setNumber(e.target.value)} placeholder={t('settings.number')} className="h-11 rounded-xl" />
                    <Input value={cap} onChange={e => setCap(e.target.value)} placeholder={t('settings.cap')} className="h-11 rounded-xl" />
                  </div>
                  <Input value={city} onChange={e => setCity(e.target.value)} placeholder={t('settings.city')} className="h-11 rounded-xl" />
                  <div className="flex justify-end gap-3">
                    <Button onClick={() => setEdit(false)} variant="outline" className="rounded-xl">{t('settings.cancel')}</Button>
                    <Button onClick={saveEdits} className="bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl">{t('settings.save')}</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* GDPR */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-semibold text-slate-900 mb-2">{t('ui.yourDataGdpr')}</h3>
              <p className="text-xs text-slate-500 mb-3">{t('ui.downloadAllData')}</p>
              <Button onClick={async () => {
                try {
                  const { data } = await exportUserData();
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `servicego_data_export_${new Date().toISOString().split('T')[0]}.json`; a.click(); URL.revokeObjectURL(url);
                  toast.success(t('ui.dataExported'));
                } catch (err) { toast.error(t('ui.dataExportError')); }
              }} variant="outline" className="w-full rounded-xl"><Download className="w-4 h-4 mr-2" />Esporta i miei dati</Button>
            </div>

            {/* Links */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-semibold text-slate-900 mb-3">{t('ui.usefulLinks')}</h3>
              <div className="space-y-2">
                <button onClick={() => nav('/termini')} className="w-full text-left p-3 rounded-xl hover:bg-slate-50 text-sm text-slate-700" data-testid="client-settings-terms">{t('settings.termsAndConditions')}</button>
                <button onClick={() => nav('/privacy')} className="w-full text-left p-3 rounded-xl hover:bg-slate-50 text-sm text-slate-700" data-testid="client-settings-privacy">{t('settings.privacyPolicy')}</button>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-semibold text-slate-900 mb-3">{t('settings.needHelp')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600"><Phone className="w-4 h-4 text-[#5848FF]"/> 800-123-456</div>
                <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-[#5848FF]"/> support@servicego.ch</div>
              </div>
            </div>

            {/* Danger */}
            <Button onClick={() => setDeleteOpen(true)} variant="destructive" className="w-full rounded-xl bg-rose-500 hover:bg-rose-600" data-testid="delete-account-button">{t('settings.deleteAccount')}</Button>
          </div>
        </div>
      </main>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-2xl bg-white" data-testid="delete-dialog">
          <DialogHeader><DialogTitle>{t('settings.deleteAccountTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">{t('settings.deleteAccountMessage')}</p>
            <Textarea data-testid="delete-reason" placeholder={t('settings.deleteReasonPlaceholder')} value={deleteReason} onChange={e => setDeleteReason(e.target.value)} className="rounded-xl" />
            <div className="grid grid-cols-1 gap-2 pt-1">
              <a data-testid="contact-solution" href={`mailto:support@servicego.ch?subject=Chiusura%20account&body=${encodeURIComponent(deleteReason || '')}`} className="rounded-xl h-11 px-4 text-center flex items-center justify-center text-white bg-emerald-500 hover:bg-emerald-600">{t('settings.evaluateSolution')}</a>
              <Button data-testid="confirm-delete-account" className="rounded-xl h-11 text-white bg-rose-500 hover:bg-rose-600" onClick={doHardDelete}>{t('settings.confirmDeleteAccount')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
